/**
 * "rain" — falling columns of characters, after the opening titles of "The Matrix": a bright head and a
 * tail fading upwards; characters change as the head moves on. Built-in effect of the DCMS2 loading
 * background (www/js/dcms-modal-loader.js, contract in docs 03-dcms2/ui-components/modal-loader.md).
 *
 * Options (DCMS.ini [modalLoader], keys rain.<option>):
 *   glyphs = katakana | binary | hex | code   character set (default katakana: half-width katakana and
 *                                             digits, mirrored like in the film)
 * ctx.intensity sets the opacity of the heads, the share of columns falling at once and the speed.
 *
 * Cost: text is never shaped per frame. Every character is drawn once into an atlas (a detached canvas,
 * in the tail colour and in the head colour with its glow, mirrored when needed) and a frame copies cells
 * of the atlas with drawImage — a few hundred to about a thousand small copies at 24 fps. The atlas is
 * rebuilt only when the colours (theme) or the pixel ratio change.
 *
 * @author AMPD Sp. z o.o.
 */

const GLYPHS = {
    katakana: 'ｦｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789:.=*+-<>¦',
    binary: '01',
    hex: '0123456789ABCDEF',
    code: '{}[]()<>/;:=+-*&|!?#$%_0123456789abcdef'
};

/**
 * alpha   — opacity of a head (the tail fades to zero), multiplied by the theme gain,
 * density — share of columns falling at once,
 * speed   — multiplier of the falling speed.
 */
const INTENSITY = {
    whisper: { alpha: 0.10, density: 0.30, speed: 0.8 },
    soft: { alpha: 0.18, density: 0.50, speed: 1 },
    bold: { alpha: 0.32, density: 0.75, speed: 1.15 }
};

const COL_W = 16;
const ROW_H = 17;
/** Room around a head cell for its glow (CSS px) */
const PAD = 6;
const FONT = '14px ui-monospace, "SF Mono", Menlo, Consolas, "MS Gothic", "Hiragino Sans", "Yu Gothic", monospace';

function randomIndex(n) {
    return Math.floor(Math.random() * n);
}

export const rainEffect = {
    fps: 24,

    create(ctx) {
        const glyphKey = GLYPHS[ctx.options.glyphs] ? ctx.options.glyphs : 'katakana';
        const glyphs = Array.from(GLYPHS[glyphKey]);
        const count = glyphs.length;
        const preset = INTENSITY[ctx.intensity] || INTENSITY.soft;
        // half-width katakana are read mirrored in the film — every character is mirrored in the atlas
        const mirror = glyphKey === 'katakana';
        const g = ctx.context;

        let cols = [];
        let rows = 0;
        let atlas = null;
        let atlasKey = '';

        /**
         * Atlas: row 0 — characters in the tail colour, row 1 — in the head colour with the glow.
         * Cells are (COL_W + 2·PAD) × (ROW_H + 2·PAD) CSS px, drawn at the pixel ratio of the canvas.
         */
        function buildAtlas(colors) {
            const dpr = ctx.dpr || 1;
            const cw = COL_W + PAD * 2;
            const ch = ROW_H + PAD * 2;
            const canvas = atlas ? atlas.canvas : ctx.canvas.ownerDocument.createElement('canvas');
            canvas.width = Math.ceil(cw * count * dpr);
            canvas.height = Math.ceil(ch * 2 * dpr);
            const a = canvas.getContext('2d');
            a.setTransform(dpr, 0, 0, dpr, 0, 0);
            a.clearRect(0, 0, cw * count, ch * 2);
            a.font = FONT;
            a.textAlign = 'center';
            a.textBaseline = 'top';
            for (let row = 0; row < 2; row++) {
                a.fillStyle = row === 0 ? colors.fg : colors.head;
                a.shadowBlur = row === 1 && colors.glow > 0 ? colors.glow : 0;
                a.shadowColor = colors.head;
                for (let i = 0; i < count; i++) {
                    a.save();
                    a.translate(i * cw + cw / 2, row * ch + PAD);
                    if (mirror) a.scale(-1, 1);
                    a.fillText(glyphs[i], 0, 0);
                    a.restore();
                }
            }
            atlas = { canvas, cw, ch, sw: cw * dpr, sh: ch * dpr, dpr };
            atlasKey = colors.key + '|' + dpr;
        }

        /**
         * New drop of a column. The average pause between drops follows the requested density.
         * initial — first deal: some columns are already falling half-way, the rest start at random.
         */
        function spawn(initial) {
            const len = 6 + randomIndex(18);
            const speed = (0.25 + Math.random() * 0.55) * preset.speed;
            const wait = ((rows + len) / speed) * (1 - preset.density) / preset.density;
            if (initial && Math.random() < preset.density) {
                return { y: Math.random() * (rows + len) - len, speed, len, wait: 0, row: -1 };
            }
            return {
                y: -Math.random() * 6,
                speed,
                len,
                wait: Math.round(wait * (initial ? Math.random() : 0.4 + Math.random() * 1.2)),
                row: -1
            };
        }

        function layout() {
            rows = Math.ceil(ctx.height / ROW_H);
            const n = Math.ceil(ctx.width / COL_W);
            for (let i = 0; i < n; i++) {
                if (!cols[i]) {
                    cols[i] = { chars: new Uint8Array(0), drop: null };
                    cols[i].drop = spawn(true);
                }
                if (cols[i].chars.length < rows) {
                    const chars = new Uint8Array(rows);
                    chars.set(cols[i].chars);
                    for (let r = cols[i].chars.length; r < rows; r++) chars[r] = randomIndex(count);
                    cols[i].chars = chars;
                }
            }
            cols.length = n;
        }

        function step() {
            for (let i = 0; i < cols.length; i++) {
                const col = cols[i];
                const d = col.drop;
                if (d.wait > 0) {
                    d.wait--;
                    continue;
                }
                d.y += d.speed;
                const head = Math.floor(d.y);
                if (head !== d.row) {
                    // the head enters a new row — a new character, as in the film
                    d.row = head;
                    if (head >= 0 && head < rows) col.chars[head] = randomIndex(count);
                }
                if (Math.random() < 0.06) {
                    // single characters of the tail change now and then
                    const r = head - 1 - randomIndex(d.len);
                    if (r >= 0 && r < rows) col.chars[r] = randomIndex(count);
                }
                if (d.y - d.len > rows) col.drop = spawn(false);
            }
        }

        function draw() {
            const colors = ctx.colors();
            if (!atlas || atlasKey !== colors.key + '|' + (ctx.dpr || 1)) buildAtlas(colors);
            const alpha = preset.alpha * colors.gain;
            const { canvas, cw, ch, sw, sh } = atlas;

            g.clearRect(0, 0, ctx.width, ctx.height);

            // tails: quadratic fade from the head upwards
            for (let i = 0; i < cols.length; i++) {
                const col = cols[i];
                const d = col.drop;
                if (d.wait > 0) continue;
                const head = Math.floor(d.y);
                const x = i * COL_W - PAD;
                for (let k = 1; k < d.len; k++) {
                    const r = head - k;
                    if (r < 0) break;
                    if (r >= rows) continue;
                    const t = 1 - k / d.len;
                    const a = alpha * t * t;
                    if (a < 0.004) break; // invisible from here up
                    g.globalAlpha = a;
                    g.drawImage(canvas, col.chars[r] * sw, 0, sw, sh, x, r * ROW_H - PAD, cw, ch);
                }
            }

            // heads: brighter, with the glow baked into the atlas
            g.globalAlpha = Math.min(1, alpha * 2.2);
            for (let i = 0; i < cols.length; i++) {
                const col = cols[i];
                const d = col.drop;
                if (d.wait > 0) continue;
                const head = Math.floor(d.y);
                if (head >= 0 && head < rows) {
                    g.drawImage(canvas, col.chars[head] * sw, sh, sw, sh, i * COL_W - PAD, head * ROW_H - PAD, cw, ch);
                }
            }
            g.globalAlpha = 1;
        }

        layout();

        return {
            resize: layout,
            frame() {
                step();
                draw();
            },
            still() {
                // reduced motion: the rain stopped mid-fall
                for (let i = 0; i < 90; i++) step();
                draw();
            },
            destroy() {
                cols = [];
                if (atlas) {
                    atlas.canvas.width = 0; // releases the bitmap at once
                    atlas = null;
                }
            }
        };
    }
};
